import type { SwipeDirection } from '@carvana/ds-carousel';
import { type RefObject, useLayoutEffect, useRef, useState } from 'react';

export type StoredDimensions = {
  carouselWidth: number;
  gapWidth: number;
  slideWidth: number;
  totalSlidesWidth: number;
};

type OwnProps = {
  carouselWidth: number;
  currentSlide: number;
  enabled: boolean;
  slidesRef: RefObject<HTMLDivElement | null>;
  storedDimensionsRef: RefObject<StoredDimensions>;
  totalSlides: number;
  visibleSlides: number;
};

type Timer = ReturnType<typeof setTimeout> | null;

const DIRECTION_THRESHOLD_PX = 10;
const MOMENTUM_OVERSHOOT_THRESHOLD = 0.85;
const SCROLL_SETTLE_MS = 80;
const SNAP_DELAY_MS = 100;

export default function usePageSnap({
  carouselWidth,
  currentSlide,
  enabled,
  slidesRef,
  storedDimensionsRef,
  totalSlides,
  visibleSlides
}: OwnProps) {
  const [endPadding, setEndPadding] = useState(0);
  const isSnappingRef = useRef(false);

  const maxPage =
    visibleSlides > 0 ? Math.ceil(totalSlides / visibleSlides) - 1 : 0;

  // Pads the end so the last page boundary is scrollable-to when the viewport is wider than a full page.
  useLayoutEffect(() => {
    if (enabled && visibleSlides > 0) {
      const { gapWidth, slideWidth } = storedDimensionsRef.current;
      const slideWithGap = slideWidth + gapWidth;
      const remainingSlidesOnLastPage = totalSlides - maxPage * visibleSlides;
      const lastPageWidth = remainingSlidesOnLastPage * slideWithGap - gapWidth;
      setEndPadding(Math.max(0, carouselWidth - lastPageWidth));
    } else {
      setEndPadding(0);
    }
  }, [
    carouselWidth,
    enabled,
    maxPage,
    storedDimensionsRef,
    totalSlides,
    visibleSlides
  ]);

  // Snaps to a page boundary when scrolling stops; uses a scroll-timeout fallback where scrollend is unsupported (iOS Safari).
  useLayoutEffect(() => {
    if (!slidesRef.current || !enabled) return;

    const node = slidesRef.current;
    let handled = false;
    let lastScrollLeft: number | null = null;
    let lastTouchX: number | null = null;
    let scrollReferenceLeft: number | null = null;
    let scrollTimer: Timer = null;
    let snapDelayTimer: Timer = null;
    let touchDirection: boolean | null = null; // true = forward, false = backward
    let touching = false;
    let touchReferenceX: number | null = null;
    let touchSnapFrameId: number | null = null;

    const supportsScrollEnd = 'onscrollend' in window;

    function getPageSize() {
      const { gapWidth, slideWidth } = storedDimensionsRef.current;

      return (slideWidth + gapWidth) * visibleSlides;
    }

    function updateScrollReference() {
      const current = node.scrollLeft;
      if (scrollReferenceLeft === null) {
        scrollReferenceLeft = current;
        lastScrollLeft = current;
      } else if (
        lastScrollLeft !== null &&
        Math.abs(current - scrollReferenceLeft) > DIRECTION_THRESHOLD_PX
      ) {
        scrollReferenceLeft = lastScrollLeft;
      }
      lastScrollLeft = current;
    }

    function resetScrollReference() {
      lastScrollLeft = null;
      scrollReferenceLeft = null;
    }

    function performSnap(scrolledForward: boolean, useDirection = false) {
      if (handled || visibleSlides <= 0) return;

      const pageSize = getPageSize();
      if (pageSize <= 0) return;

      handled = true;
      resetScrollReference();

      const scrollLeft = node.scrollLeft;
      const pageProgress = (scrollLeft % pageSize) / pageSize;
      // Near the end of a page (momentum carried past boundary) — snap to nearest
      const momentumOvershoot =
        !useDirection &&
        scrolledForward &&
        pageProgress > MOMENTUM_OVERSHOOT_THRESHOLD;

      let snapPage: number;
      if (momentumOvershoot) {
        snapPage = Math.round(scrollLeft / pageSize);
      } else {
        snapPage = scrolledForward
          ? Math.ceil(scrollLeft / pageSize)
          : Math.floor(scrollLeft / pageSize);
      }
      snapPage = Math.max(0, Math.min(maxPage, snapPage));

      const targetPosition = snapPage * pageSize;
      if (Math.abs(scrollLeft - targetPosition) > 1) {
        isSnappingRef.current = true;
        node.scrollTo({ behavior: 'smooth', left: targetPosition });
      }
    }

    function handleScroll() {
      // Without scrollend (iOS Safari), the scroll-settle timeout stands in for scrollend to reset isSnappingRef.
      if (isSnappingRef.current) {
        if (!supportsScrollEnd) {
          if (scrollTimer) clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => {
            isSnappingRef.current = false;
          }, SCROLL_SETTLE_MS);
        }

        return;
      }

      if (snapDelayTimer) {
        clearTimeout(snapDelayTimer);
        snapDelayTimer = null;
      }

      handled = false;
      updateScrollReference();

      // Wait for momentum to stop, then snap. Don't fire while touching — touchend handles that.
      if (!supportsScrollEnd && !touching) {
        if (scrollTimer) clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          if (!slidesRef.current) return;
          const reference = scrollReferenceLeft ?? node.scrollLeft;
          const hasTouchDirection = touchDirection !== null;
          const direction = touchDirection ?? node.scrollLeft > reference;
          touchDirection = null;
          performSnap(direction, hasTouchDirection);
        }, SCROLL_SETTLE_MS);
      }
    }

    function handleScrollEnd() {
      if (isSnappingRef.current) {
        isSnappingRef.current = false;

        return;
      }

      if (scrollTimer) clearTimeout(scrollTimer);

      const reference = scrollReferenceLeft ?? node.scrollLeft;
      const direction = node.scrollLeft > reference;
      const pageSize = getPageSize();
      if (pageSize <= 0) return;

      const currentPage = Math.round(node.scrollLeft / pageSize);
      const atBoundary = currentPage >= maxPage || currentPage <= 0;

      if (atBoundary) {
        performSnap(direction);
      } else {
        if (snapDelayTimer) clearTimeout(snapDelayTimer);
        snapDelayTimer = setTimeout(() => {
          performSnap(direction);
        }, SNAP_DELAY_MS);
      }
    }

    // Only moves the touch reference point after 10px+ so end-of-swipe jitter doesn't flip direction.
    function handleTouchStart(event: TouchEvent) {
      handled = false;
      isSnappingRef.current = false;
      lastTouchX = event.touches[0].clientX;
      touchDirection = null;
      touching = true;
      touchReferenceX = lastTouchX;
    }

    function handleTouchMove(event: TouchEvent) {
      const x = event.touches[0].clientX;
      if (
        touchReferenceX !== null &&
        Math.abs(x - touchReferenceX) > DIRECTION_THRESHOLD_PX
      ) {
        touchReferenceX = lastTouchX;
      }
      lastTouchX = x;
    }

    function handleTouchEnd() {
      touching = false;

      if (touchReferenceX !== null && lastTouchX !== null) {
        const swipedForward = lastTouchX < touchReferenceX;
        const pageSize = getPageSize();
        if (pageSize <= 0) return;

        const currentPage = Math.round(node.scrollLeft / pageSize);
        const atBoundary =
          (currentPage >= maxPage && swipedForward) ||
          (currentPage <= 0 && !swipedForward);

        if (atBoundary) {
          // At boundaries: let momentum + rubber-band finish, then snap
          touchDirection = swipedForward;
        } else {
          // Middle pages: freeze momentum, then snap on next frame
          if (scrollTimer) clearTimeout(scrollTimer);
          node.scrollTo({ left: node.scrollLeft });
          touchSnapFrameId = requestAnimationFrame(() => {
            performSnap(swipedForward, true);
          });
        }
      }

      lastTouchX = null;
      touchReferenceX = null;
    }

    node.addEventListener('scroll', handleScroll);
    node.addEventListener('touchstart', handleTouchStart, { passive: true });
    node.addEventListener('touchmove', handleTouchMove, { passive: true });
    node.addEventListener('touchend', handleTouchEnd);
    node.addEventListener('touchcancel', handleTouchEnd);
    if (supportsScrollEnd) node.addEventListener('scrollend', handleScrollEnd);

    return () => {
      node.removeEventListener('scroll', handleScroll);
      node.removeEventListener('touchstart', handleTouchStart);
      node.removeEventListener('touchmove', handleTouchMove);
      node.removeEventListener('touchend', handleTouchEnd);
      node.removeEventListener('touchcancel', handleTouchEnd);
      if (supportsScrollEnd)
        node.removeEventListener('scrollend', handleScrollEnd);
      if (scrollTimer) clearTimeout(scrollTimer);
      if (snapDelayTimer) clearTimeout(snapDelayTimer);
      if (touchSnapFrameId) cancelAnimationFrame(touchSnapFrameId);
      resetScrollReference();
    };
  }, [
    enabled,
    maxPage,
    slidesRef,
    storedDimensionsRef,
    totalSlides,
    visibleSlides
  ]);

  // Snap to last page boundary on resize when the last card is visible
  useLayoutEffect(() => {
    const slidesNode = slidesRef.current;
    if (!slidesNode) return;

    const frameId = requestAnimationFrame(() => {
      if (enabled) {
        const { gapWidth, slideWidth } = storedDimensionsRef.current;
        const pageSize = (slideWidth + gapWidth) * visibleSlides;
        if (pageSize <= 0) return;

        const currentPage = Math.round(slidesNode.scrollLeft / pageSize);
        if (currentPage >= maxPage) {
          slidesNode.scrollTo({ behavior: 'smooth', left: maxPage * pageSize });
        }
      }
      slidesNode.dispatchEvent(new Event('scroll'));
    });

    return () => cancelAnimationFrame(frameId);
  }, [enabled, maxPage, slidesRef, storedDimensionsRef, visibleSlides]);

  const paginationTotal = enabled ? maxPage + 1 : totalSlides;
  const paginationCurrent =
    enabled && visibleSlides > 0
      ? Math.min(Math.floor(currentSlide / visibleSlides) + 1, paginationTotal)
      : currentSlide;

  function scrollToPage(direction: SwipeDirection) {
    const slidesNode = slidesRef.current;
    if (!slidesNode) return;

    const { gapWidth, slideWidth } = storedDimensionsRef.current;
    const pageSize = (slideWidth + gapWidth) * visibleSlides;
    if (pageSize <= 0) return;

    const currentPage = Math.round(slidesNode.scrollLeft / pageSize);
    const targetPage = Math.max(
      0,
      Math.min(maxPage, currentPage + (direction === 'next' ? 1 : -1))
    );
    const targetPosition = targetPage * pageSize;

    if (Math.abs(slidesNode.scrollLeft - targetPosition) > 1) {
      isSnappingRef.current = true;
      slidesNode.scrollTo({ behavior: 'smooth', left: targetPosition });
    }
  }

  return { endPadding, paginationCurrent, paginationTotal, scrollToPage };
}
